import React from "react";
import { Box, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import "dayjs/locale/it";
import { CalendarClock, CheckCircle2, PlayCircle, Users } from "lucide-react";
import BadgeBoxCst from "@components/BadgeBoxCst";
import MoreActionsMenuCst from "@components/MoreActionsMenuCst";
import { ListItemAction } from "@components/ListItemCardCst/types";
import { EditionItem, EditionStatus } from "../types";
import {
  EDITION_STATUS_VARIANT,
  deriveEditionStatus,
} from "../config/editionStatus";
import { ButtonSecondary } from "src/UI/ButtonSecondary";

interface EditionCardProps {
  edition: EditionItem;
  onManage?: (edition: EditionItem) => void;
  menuActions?: ListItemAction[] | ListItemAction[][];
}

const STATUS_ICON: Record<EditionStatus, React.ReactNode> = {
  planned: <CalendarClock size={13} />,
  "in-progress": <PlayCircle size={13} />,
  ended: <CheckCircle2 size={13} />,
};

const formatRange = (start?: number, end?: number): string => {
  if (!start || !end) return "";
  const s = dayjs(start).locale("it");
  const e = dayjs(end).locale("it");
  return `Dal ${s.format("D MMMM YYYY")} al ${e.format("D MMMM YYYY")}`;
};

const EditionCard: React.FC<EditionCardProps> = ({
  edition,
  onManage,
  menuActions,
}) => {
  const { t } = useTranslation("editions");
  const theme = useTheme();

  const status = deriveEditionStatus(edition.start_date, edition.end_date);
  const occupiedPlaces = edition.occupied_places ?? 0;
  const totalPlaces = edition.total_places ?? 0;
  const hasPlaces = !!occupiedPlaces || !!totalPlaces;
  const editionCode = edition.edition_code || String(t("card.no-code"));
  const subtitle = edition.edition_name
    ? `${editionCode} | ID: ${edition.id}`
    : editionCode;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "stretch",
        justifyContent: "space-between",
        gap: 2,
        padding: "20px 24px",
        borderRadius: "12px",
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
        [theme.breakpoints.down("lg")]: {
          flexDirection: "column",
          alignItems: "stretch",
          gap: 1.5,
        },
      }}
    >
      <Box
        sx={{ display: "flex", flexDirection: "column", gap: 0.5, minWidth: 0 }}
      >
        <Typography
          sx={{ fontSize: "12px", color: theme.palette.text.primary }}
        >
          {formatRange(edition.start_date, edition.end_date)}
        </Typography>
        <Typography
          sx={{
            fontSize: "16px",
            fontWeight: 700,
            color: theme.palette.text.primary,
          }}
        >
          {edition.edition_name || `#${edition.id}`}
        </Typography>
        <Typography
          sx={{ fontSize: "12px", color: theme.palette.text.primary }}
        >
          {subtitle}
        </Typography>
        {hasPlaces && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              mt: 0.5,
              color: theme.palette.info.main,
            }}
          >
            <Users size={14} />
            <Typography sx={{ fontSize: "13px", fontWeight: 600 }}>
              {t("card.places", {
                capacity: `${occupiedPlaces}/${totalPlaces}`,
                interpolation: { escapeValue: false },
              })}
            </Typography>
          </Box>
        )}
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-end",
          justifyContent: "space-between",
          flexShrink: 0,
          gap: 1.5,
          [theme.breakpoints.down("lg")]: {
            width: "100%",
            alignItems: "stretch",
          },
        }}
      >
        <Box
          sx={{
            [theme.breakpoints.down("lg")]: { alignSelf: "flex-start" },
          }}
        >
          <BadgeBoxCst
            variant={EDITION_STATUS_VARIANT[status]}
            icon={STATUS_ICON[status]}
          >
            {t(`status.${status}`)}
          </BadgeBoxCst>
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            [theme.breakpoints.down("lg")]: {
              justifyContent: "flex-start",
              flexWrap: "wrap",
            },
          }}
        >
          <ButtonSecondary size="md" onClick={() => onManage?.(edition)}>
            {t("card.manage")}
          </ButtonSecondary>
          {menuActions && (
            <MoreActionsMenuCst
              actions={menuActions}
              ariaLabel="edition actions"
            />
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default EditionCard;
